<template>
  <Modal :show="true" @close-modal="$emit('close-modal')" class="am-modal">
    <div class="add-team-member">
      <form @submit.prevent="submitForm" class="add-team-member__form">
        <h1 class="add-team-member__title">Add new Team Member</h1>
        <p class="add-team-member__subtitle">
          Add a new Team member and choose a privilege that best suits them
        </p>

        <div class="add-team-member__fields">
          <div class="add-team-member__field">
            <label for="fname" class="add-team-member__label">First name</label>
            <input
              id="fname"
              v-model="form.fname"
              placeholder="Enter First Name"
              type="text"
              class="add-team-member__input"
              :class="{ 'add-team-member__input--error': errors.fname && submitted }"
              required
            />
            <p v-if="errors.fname && submitted" class="add-team-member__error">
              {{ errors.fname }}
            </p>
          </div>

          <div class="add-team-member__field">
            <label for="lname" class="add-team-member__label">Last name</label>
            <input
              id="lname"
              v-model="form.lname"
              type="text"
              placeholder="Enter Last Name"
              class="add-team-member__input"
              :class="{ 'add-team-member__input--error': errors.lname && submitted }"
              required
            />
            <p v-if="errors.lname && submitted" class="add-team-member__error">
              {{ errors.lname }}
            </p>
          </div>

          <div class="add-team-member__field">
            <label for="email" class="add-team-member__label">Email</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              placeholder="Enter Email"
              class="add-team-member__input"
              :class="{ 'add-team-member__input--error': errors.email && submitted }"
              required
            />
            <p v-if="errors.email && submitted" class="add-team-member__error">
              {{ errors.email }}
            </p>
          </div>

          <div class="add-team-member__field">
            <label for="role" class="add-team-member__label">Role</label>
            <div class="add-team-member__select-wrapper">
              <select
                id="role"
                v-model="form.role"
                class="add-team-member__select"
                :class="{ 'add-team-member__select--error': errors.role && submitted }"
                required
              >
                <option value="" disabled>Select role</option>
                <option
                  v-for="(role, index) in roles"
                  :key="index"
                  :value="role.name"
                >
                  {{ role.name }}
                </option>
              </select>
              <svg class="add-team-member__select-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.99992 2.82901L7.82792 1.33514e-05L9.24292 1.41401L4.99992 5.65701L0.75692 1.41401L2.17192 1.33514e-05L4.99992 2.82901Z" fill="#64748B"/>
              </svg>
            </div>
            <p v-if="errors.role && submitted" class="add-team-member__error">
              {{ errors.role }}
            </p>
          </div>
        </div>

        <div v-if="form.role" class="add-team-member__permissions-toggle">
          <button
            type="button"
            @click="showPermissions = !showPermissions"
            class="add-team-member__toggle-btn"
          >
            <span>{{ showPermissions ? "Hide" : "View" }} Role Privileges</span>
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="{ 'add-team-member__toggle-icon--rotated': !showPermissions }"
              class="add-team-member__toggle-icon"
            >
              <path d="M1 5L5 1L9 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div v-if="showPermissions && form.role" class="add-team-member__permissions">
          <div v-if="filteredServices.length === 0" class="add-team-member__permissions-empty">
            No permissions assigned to this role
          </div>
          <div
            v-for="service in filteredServices"
            :key="service.service"
            class="add-team-member__service"
          >
            <div
              class="add-team-member__service-header"
              @click="toggleServiceExpand(service)"
            >
              <div class="add-team-member__service-info">
                <div class="add-team-member__checkbox">
                  <div
                    class="add-team-member__checkbox-indicator"
                    :class="{ 'add-team-member__checkbox-indicator--checked': hasServicePermission(service) }"
                  ></div>
                </div>
                <span class="add-team-member__service-name">
                  {{ formatServiceName(service.service) }}
                </span>
              </div>
              <svg
                class="add-team-member__expand-icon"
                :class="{ 'add-team-member__expand-icon--expanded': service.expanded }"
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 1L5 5L9 1" stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="add-team-member__service-actions" v-if="service.expanded">
              <div
                v-for="action in service.actions"
                :key="action"
                class="add-team-member__action-item"
              >
                <span class="add-team-member__action-label">{{ formatActionName(action) }}</span>
                <div
                  class="add-team-member__toggle-switch"
                  :class="{ 'add-team-member__toggle-switch--active': hasActionPermission(service.service, action) }"
                >
                  <div class="add-team-member__toggle-indicator"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="add-team-member__actions">
          <button
            type="button"
            @click="$emit('close-modal')"
            class="add-team-member__btn add-team-member__btn--cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="add-team-member__btn add-team-member__btn--submit"
            :disabled="loading"
          >
            <span v-if="loading" class="add-team-member__spinner"></span>
            <span v-else>Add Member</span>
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>

<script setup>
import Modal from "@/components/general/Modal";
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useGetters } from "@/composables/useMapper";


const emit = defineEmits(["close-modal", "success"]);
const store = useStore();
const loading = computed(() => store.getters["loading"]);
const orgDetails = computed(
  () => store.getters["organisationModule/organisationDetails"]
);

const { iamGroups } = useGetters('organisationModule', [
  'iamGroups'
]);

const roles = computed(() => iamGroups.value?.docs || []);

const form = reactive({
  fname: "",
  lname: "",
  email: "",
  role: "",
});

const errors = reactive({
  fname: "",
  lname: "",
  email: "",
  role: "",
});
const showPermissions = ref(false);
const rolePermissions = ref([]);

const allServices = ref([
  {
    service: "deal",
    actions: ["addDeal", "updateDeal", "viewDeal"],
    expanded: false,
  },
  {
    service: "spv",
    actions: ["addSpv", "updateSpv", "viewSpv"],
    expanded: false,
  },
  {
    service: "member",
    actions: ["addMember", "updateMember", "viewMember"],
    expanded: false,
  },
  {
    service: "wallet",
    actions: [
      "fundWallet",
      "withdrawWallet",
      "buyWallet",
      "sellWallet",
      "viewWallet",
    ],
    expanded: false,
  },
  {
    service: "team",
    actions: [
      "addMember",
      "updateMember",
      "viewMember",
      "addGroup",
      "updateGroup",
      "viewGroup",
    ],
    expanded: false,
  },
  {
    service: "kyc",
    actions: ["addKyc", "updateKyc", "viewKyc"],
    expanded: false,
  },
  {
    service: "bank",
    actions: ["addBank", "updateBank", "viewBank"],
    expanded: false,
  },
  {
    service: "configuration",
    actions: ["addConfiguration", "updateConfiguration", "viewConfiguration"],
    expanded: false,
  },
  {
    service: "subscription",
    actions: [
      "addSubscription",
      "updateSubscription",
      "viewSubscription",
      "deleteSubscription",
    ],
    expanded: false,
  },
]);

const filteredServices = computed(() => {
  if (rolePermissions.value.includes("*:*")) {
    return allServices.value;
  }

  return allServices.value.filter((service) => {
    if (rolePermissions.value.includes(`${service.service}:*`)) {
      return true;
    }

    return service.actions.some((action) =>
      rolePermissions.value.includes(`${service.service}:${action}`)
    );
  });
});

const submitted = ref(false);

function validateForm() {
  let isValid = true;

  errors.fname = "";
  errors.lname = "";
  errors.email = "";
  errors.role = "";

  if (!form.fname.trim()) {
    errors.fname = "First name is required";
    isValid = false;
  }

  if (!form.lname.trim()) {
    errors.lname = "Last name is required";
    isValid = false;
  }

  if (!form.email.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Please enter a valid email";
    isValid = false;
  }

  if (!form.role) {
    errors.role = "Please select a role";
    isValid = false;
  }

  return isValid;
}

async function submitForm() {
  submitted.value = true;

  if (!validateForm()) {
    return;
  }

  const role = roles.value.find((r) => r.name === form.role);

  try {
    await store.dispatch("organisationModule/addMemberToTeam", {
      payload: {
        first_name: form.fname,
        last_name: form.lname,
        email: form.email,
        groups: [role.id]
      },
      id: orgDetails.value._id,
    });
    emit("success");
  } catch (error) {
    console.error("Failed to add team member:", error);
  }
}

const formatServiceName = (service) => {
  return service.charAt(0).toUpperCase() + service.slice(1);
};

const formatActionName = (action) => {
  return action
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

const toggleServiceExpand = (service) => {
  service.expanded = !service.expanded;
};

const extractRolePermissions = () => {
  rolePermissions.value = [];

  if (!form.role) return;

  const selectedRoleObj = roles.value.find((r) => r.name === form.role);

  if (!selectedRoleObj) return;

  if (
    selectedRoleObj.permissions &&
    Array.isArray(selectedRoleObj.permissions)
  ) {
    selectedRoleObj.permissions.forEach((permissionObj) => {
      if (
        permissionObj.actions &&
        Array.isArray(permissionObj.actions) &&
        permissionObj.effect === "ALLOW"
      ) {
        rolePermissions.value.push(...permissionObj.actions);
      }
    });
  }
};

const hasServicePermission = (service) => {
  const hasAllStarPermission =
    rolePermissions.value.includes(`${service.service}:*`) ||
    rolePermissions.value.includes("*:*");
  if (hasAllStarPermission) return true;

  return service.actions.some((action) =>
    rolePermissions.value.includes(`${service.service}:${action}`)
  );
};

const hasActionPermission = (service, action) => {
  const actionString = `${service}:${action}`;
  const hasAllStarPermission =
    rolePermissions.value.includes(`${service}:*`) ||
    rolePermissions.value.includes("*:*");

  return hasAllStarPermission || rolePermissions.value.includes(actionString);
};

watch(
  () => form.role,
  (newRole) => {
    if (newRole) {
      extractRolePermissions();
      showPermissions.value = true;
    } else {
      rolePermissions.value = [];
      showPermissions.value = false;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.add-team-member {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  padding: 8px 0;

  &__form {
    width: 100%;
  }

  &__title {
    font-size: 20px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__subtitle {
    font-size: 14px;
    color: #64748B;
    margin: 0;
    line-height: 1.5;
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-top: 32px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 11px;
    font-weight: 500;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__input {
    height: 48px;
    padding: 0 16px;
    font-size: 14px;
    color: #0F172A;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    outline: none;
    transition: border-color 0.15s ease;

    &::placeholder {
      color: #94A3B8;
    }

    &:focus {
      border-color: #001240;
    }

    &--error {
      border-color: #EF4444;
    }
  }

  &__select-wrapper {
    position: relative;
  }

  &__select {
    width: 100%;
    height: 48px;
    padding: 0 40px 0 16px;
    font-size: 14px;
    color: #0F172A;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    outline: none;
    cursor: pointer;
    appearance: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: #001240;
    }

    &--error {
      border-color: #EF4444;
    }
  }

  &__select-arrow {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  &__error {
    font-size: 12px;
    color: #EF4444;
    margin: 0;
  }

  &__permissions-toggle {
    margin: 24px 0;
    display: flex;
    justify-content: center;
  }

  &__toggle-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    font-size: 14px;
    font-weight: 500;
    color: #001240;
    background: none;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__toggle-icon {
    transition: transform 0.2s ease;
    color: #64748B;

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__permissions {
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    padding: 8px;
    margin-bottom: 24px;
    max-height: 320px;
    overflow-y: auto;
  }

  &__permissions-empty {
    padding: 24px;
    text-align: center;
    font-size: 14px;
    color: #64748B;
  }

  &__service {
    border-bottom: 1px solid #F1F5F9;

    &:last-child {
      border-bottom: none;
    }
  }

  &__service-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 8px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border-radius: 8px;

    &:hover {
      background-color: #F8FAFC;
    }
  }

  &__service-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__checkbox {
    flex-shrink: 0;
  }

  &__checkbox-indicator {
    width: 20px;
    height: 20px;
    border: 2px solid #CBD5E1;
    border-radius: 50%;
    background: #FFFFFF;
    transition: all 0.15s ease;

    &--checked {
      border-color: #10B981;
      background: #10B981;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 8px;
        height: 8px;
        background: #FFFFFF;
        border-radius: 50%;
      }
    }
  }

  &__service-name {
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    user-select: none;
  }

  &__expand-icon {
    transition: transform 0.2s ease;

    &--expanded {
      transform: rotate(180deg);
    }
  }

  &__service-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 8px 16px 40px;
    border-top: 1px solid #F1F5F9;
  }

  &__action-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 160px;
    padding: 8px 12px;
    background: #F8FAFC;
    border-radius: 8px;
  }

  &__action-label {
    font-size: 13px;
    color: #475569;
    user-select: none;
  }

  &__toggle-switch {
    width: 36px;
    height: 20px;
    background-color: #E2E8F0;
    border-radius: 10px;
    position: relative;
    cursor: default;
    transition: background-color 0.2s ease;

    &--active {
      background-color: #D1FAE5;
    }
  }

  &__toggle-indicator {
    width: 16px;
    height: 16px;
    background-color: #FFFFFF;
    border-radius: 50%;
    position: absolute;
    top: 2px;
    left: 2px;
    transition: transform 0.2s ease;

    .add-team-member__toggle-switch--active & {
      transform: translateX(16px);
      background-color: #10B981;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 32px;
  }

  &__btn {
    height: 48px;
    padding: 0 32px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &--cancel {
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      color: #475569;

      &:hover {
        background: #E2E8F0;
      }
    }

    &--submit {
      background: #001240;
      border: none;
      color: #FFFFFF;
      min-width: 140px;

      &:hover:not(:disabled) {
        background: #001a5c;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #FFFFFF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>